// src/utils/seoUtils.js
import { head } from '@/plugins/head'
import { debugWarn } from '@/utils/logger';

export const SITE_URL = 'https://sejarah-thumann.online';
export const SEO_ENTITY_IDS = Object.freeze({
    organization: `${SITE_URL}/#organization`,
    jeffreyThumann: `${SITE_URL}/#jeffrey-thumann`,
    website: `${SITE_URL}/#website`,
});

// Track active head entries by logical key so repeated calls replace prior tags
// instead of stacking duplicate meta/canonical elements.
const activeEntries = new Map();

function applyHead(key, headObject) {
    const existingEntry = activeEntries.get(key);
    if (existingEntry && typeof existingEntry.dispose === 'function') {
        // Disposing prevents stale tags from previous route/page state.
        existingEntry.dispose();
    }
    activeEntries.set(key, head.push(headObject));
}

/**
 * Build hreflang link tags for multilingual pages
 * @param {Object} params
 * @param {string} params.currentLocale - Current locale (e.g., 'nl', 'en', 'id')
 * @param {string} params.route - Current route path without locale prefix
 * @param {Array<string>} [params.availableLocales=['nl', 'en', 'id']] - Available locales
 * @param {string} [params.baseUrl='https://sejarah-thumann.online'] - Base URL for absolute URLs
 * @returns {Array<Object>} Array of hreflang link objects
 */
export function buildHreflangs({ route, availableLocales = ['nl', 'en', 'id'], baseUrl = 'https://sejarah-thumann.online' }) {
    const hreflangs = [];

    // Clean route: remove leading/trailing slashes
    const cleanRoute = route.replace(/^\/+|\/+$/g, '');

    availableLocales.forEach(locale => {
        let href;
        if (locale === 'nl') {
            // Dutch (default) - no locale prefix
            href = cleanRoute ? `${baseUrl}/${cleanRoute}` : `${baseUrl}/`;
        } else {
            // Other locales - include locale prefix
            href = cleanRoute ? `${baseUrl}/${locale}/${cleanRoute}` : `${baseUrl}/${locale}`;
        }

        hreflangs.push({
            rel: 'alternate',
            hreflang: locale,
            href: href
        });
    });

    // Add x-default for search engines (typically points to default locale)
    hreflangs.push({
        rel: 'alternate',
        hreflang: 'x-default',
        href: cleanRoute ? `${baseUrl}/${cleanRoute}` : `${baseUrl}/`
    });

    return hreflangs;
}

/**
 * Build canonical URL based on locale and route
 * @param {Object} params
 * @param {string} params.currentLocale - Current locale
 * @param {string} params.route - Current route
 * @param {string} [params.baseUrl='https://sejarah-thumann.online'] - Base URL
 * @returns {string} Canonical URL
 */
export function buildCanonicalUrl({ currentLocale, route, baseUrl = 'https://sejarah-thumann.online' }) {
    const cleanRoute = route.replace(/^\/+|\/+$/g, '');

    if (currentLocale === 'nl') {
        // Default locale: no prefix
        return cleanRoute ? `${baseUrl}/${cleanRoute}` : `${baseUrl}/`;
    } else {
        // Other locales: include prefix
        return cleanRoute ? `${baseUrl}/${currentLocale}/${cleanRoute}` : `${baseUrl}/${currentLocale}`;
    }
}



/**
 * Apply generic SEO tags.
 * Use this for pages like About, Contact, Photos, Stories, etc.
 * Composable must be called within a component setup context.
 * 
 * @param {Object} params
 * @param {string} params.title - The title for the page.
 * @param {string} params.description - The meta description.
 * @param {string} params.image - The URL for the image to use in social previews.
 * @param {string} [params.currentLocale='nl'] - Current locale for canonical URL.
 * @param {string} [params.route] - Current route path for canonical URL.
 * @param {string} [params.canonicalUrl] - Optional override for canonical URL.
 * @param {string} [params.type="website"] - The Open Graph type (website, article, etc.).
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.includeTwitter=false] - Whether to include Twitter Card meta tags.
 * @param {boolean} [options.includeHreflangs=false] - Whether to include hreflang tags.
 * @returns {void}
 */
export function applyGenericSeo(params, options = {}) {
    const {
        title,
        description,
        image,
        currentLocale = 'nl',
        route = '',
        url,
        canonicalUrl: overrideCanonical,
        type = 'website'
    } = params;

    if (!title || !description || !image) {
        debugWarn('applyGenericSeo: Missing required parameters (title, description, or image)');
        return;
    }

    // Build canonical URL
    const explicitCanonical = overrideCanonical || url;
    let canonicalUrl;
    if (explicitCanonical) {
        const parsed = new URL(explicitCanonical, 'https://sejarah-thumann.online');
        const normalizedPath = parsed.pathname === '/' ? '/' : parsed.pathname.replace(/\/+$/, '');
        canonicalUrl = `https://sejarah-thumann.online${normalizedPath}`;
    } else {
        canonicalUrl = buildCanonicalUrl({
            currentLocale,
            route,
            baseUrl: 'https://sejarah-thumann.online'
        });
    }

    // Build head object
    const headObject = {
        title: title,
        meta: [
            { name: 'description', content: description },
            { property: 'og:type', content: type },
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:image', content: image },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'og:image:type', content: 'image/jpeg' },
            { property: 'og:url', content: canonicalUrl },
        ],
        link: [
            { rel: 'canonical', href: canonicalUrl }
        ]
    };

    // Add Twitter tags if requested
    if (options.includeTwitter) {
        headObject.meta.push(
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: image },
            { name: 'twitter:card', content: 'summary_large_image' }
        );
    }

    // Add hreflang tags if requested
    if (options.includeHreflangs) {
        const hreflangs = buildHreflangs({
            currentLocale,
            route,
            baseUrl: 'https://sejarah-thumann.online'
        });
        headObject.link.push(...hreflangs);
    }

    applyHead('generic-seo', headObject);
}

/**
 * Apply SEO tags for a blog post.
 * Deze functie stelt de document title en relevante meta tags in voor SEO, Open Graph, 
 * Twitter Cards en voegt optioneel gestructureerde data (JSON-LD) toe voor een blogpost.
 *
 * @param {Object} post - Het blogpost-object met benodigde SEO-gegevens.
 * @param {string} post.title - De titel van de blogpost.
 * @param {string} [post.summary] - Een samenvatting of excerpt van de blogpost, gebruikt voor de meta description.
 * @param {Object|string} [post.image] - De hoofdafbeelding van de post. Als een object, moet dit een "url" property hebben; als string, dan direct de URL.
 * @param {Array} [post.images] - Een array met extra afbeeldingen (als fallback). Elementen kunnen strings zijn of objecten met een "url" property.
 * @param {string} post.slug - De slug van de blogpost, gebruikt om de canonical URL op te bouwen.
 * @param {string} [post.author] - De naam van de auteur van de post.
 * @param {string} [post.published] - De publicatiedatum (ISO-formaat) van de post.
 * @param {Array} [post.categories] - Een array met categorie-objecten. Elk object dient een "attributes" property te hebben met een "Name" veld.
 * @param {string} [post.currentLocale='nl'] - Current locale for canonical URL.
 * @param {Object} [options] - Optionele instellingen.
 * @param {boolean} [options.includeTwitter=false] - Of Twitter Card meta tags moeten worden ingesteld.
 * @param {boolean} [options.includeJsonLd=false] - Of JSON-LD gestructureerde data voor de blogpost moet worden toegevoegd.
 * @param {boolean} [options.includeHreflangs=false] - Whether to include hreflang tags.
 */
export function applyBlogPostSeo(post, options = {}) {
    if (!post) return;

    const title = `${post.title} | Sejarah Thümann`;
    const description = post.summary || 'Lees het volledige verhaal op Sejarah Thümann.';

    // Bepaal de hoofdafbeelding: gebruik post.image als deze bestaat, anders de eerste uit post.images of fallback.
    const mainImage = post.image
        ? (typeof post.image === 'string' ? post.image : post.image.url)
        : (post.images?.[0] || `https://sejarah-thumann.online/default-image.jpg`);
    const imageUrl = mainImage.startsWith('http') ? mainImage : `https://sejarah-thumann.online${mainImage}`;

    // Bouw de canonical URL op basis van de slug
    const slug = post.slug || '';
    const currentLocale = post.currentLocale || options.currentLocale || 'nl';
    const canonicalUrl = buildCanonicalUrl({
        currentLocale,
        route: `/verhalen/${slug}`,
        baseUrl: 'https://sejarah-thumann.online'
    });

    // Link known authors to the canonical site entity. Unknown bylines are
    // omitted instead of being represented as fictional Person entities.
    const authorName = typeof post.author === 'string' ? post.author.trim() : '';
    const normalizedAuthorName = authorName
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    const unknownAuthorNames = new Set(['onbekend', 'unknown', 'unknown author']);
    let author;
    if (normalizedAuthorName === 'jeffrey thumann') {
        author = { "@id": SEO_ENTITY_IDS.jeffreyThumann };
    } else if (authorName && !unknownAuthorNames.has(normalizedAuthorName)) {
        author = {
            "@type": "Person",
            "name": authorName
        };
    }

    // Build JSON-LD structured data if requested
    let jsonLdScript = null;
    if (options.includeJsonLd) {
        const blogPosting = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "@id": `${canonicalUrl}#blog-posting`,
            "headline": post.title,
            "description": description,
            "image": [imageUrl],
            "datePublished": post.published,
            "url": canonicalUrl,
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": canonicalUrl
            },
            "publisher": {
                "@id": SEO_ENTITY_IDS.organization
            },
            "keywords": post.categories
                ?.map(cat => cat.attributes?.Name)
                .filter(Boolean)
                .join(', ')
        };

        if (author) blogPosting.author = author;
        if (post.edited) blogPosting.dateModified = post.edited;

        jsonLdScript = {
            type: 'application/ld+json',
            children: JSON.stringify(blogPosting)
        };
    }

    const headObject = {
        title: title,
        meta: [
            { name: 'description', content: description },
            { property: 'og:type', content: 'article' },
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:image', content: imageUrl },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'og:image:type', content: 'image/jpeg' },
            { property: 'og:url', content: canonicalUrl },
        ],
        link: [
            { rel: 'canonical', href: canonicalUrl }
        ]
    };

    // Add Twitter tags if requested
    if (options.includeTwitter) {
        headObject.meta.push(
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: imageUrl },
            { name: 'twitter:card', content: 'summary_large_image' }
        );
    }

    // Add hreflang tags if requested
    if (options.includeHreflangs) {
        const hreflangs = buildHreflangs({
            currentLocale,
            route: `/verhalen/${slug}`,
            baseUrl: 'https://sejarah-thumann.online'
        });
        headObject.link.push(...hreflangs);
    }

    // Add JSON-LD if generated
    if (jsonLdScript) {
        headObject.script = [jsonLdScript];
    }

    applyHead('blog-seo', headObject);
}

/**
 * Apply SEO tags and structured data for calendar/event pages.
 * 
 * @param {Object} eventData - The event data object.
 * @param {string} eventData.title - The title of the event.
 * @param {string} eventData.description - Description of the event.
 * @param {string} eventData.date - The event date (ISO format or similar).
 * @param {string} [eventData.image] - Image URL for the event.
 * @param {string} [eventData.currentLocale='nl'] - Current locale for canonical URL.
 * @param {string} [eventData.canonicalUrl] - Optional override for canonical URL.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.includeTwitter=false] - Whether to include Twitter Card meta tags.
 * @param {boolean} [options.includeJsonLd=false] - Whether to include JSON-LD structured data.
 * @param {boolean} [options.includeHreflangs=false] - Whether to include hreflang tags.
 */
export function applyEventSeo(eventData, options = {}) {
    if (!eventData) return;

    const title = `${eventData.title} | Sejarah Thümann`;
    const description = eventData.description || eventData.title;
    const image = eventData.image || `https://sejarah-thumann.online/default-image.jpg`;
    const currentLocale = eventData.currentLocale || 'nl';

    const canonicalUrl = eventData.canonicalUrl || eventData.url || buildCanonicalUrl({
        currentLocale,
        route: eventData.route || '',
        baseUrl: 'https://sejarah-thumann.online'
    });

    // Build JSON-LD structured data if requested
    let jsonLdScript = null;
    if (options.includeJsonLd) {
        jsonLdScript = {
            type: 'application/ld+json',
            children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Event",
                "name": eventData.title,
                "description": description,
                "image": image,
                "startDate": eventData.date,
                "url": canonicalUrl
            })
        };
    }

    const headObject = {
        title: title,
        meta: [
            { name: 'description', content: description },
            { property: 'og:type', content: 'article' },
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:image', content: image },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'og:image:type', content: 'image/jpeg' },
            { property: 'og:url', content: canonicalUrl },
        ],
        link: [
            { rel: 'canonical', href: canonicalUrl }
        ]
    };

    if (options.includeTwitter) {
        headObject.meta.push(
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: image },
            { name: 'twitter:card', content: 'summary_large_image' }
        );
    }

    if (options.includeHreflangs && eventData.route) {
        const hreflangs = buildHreflangs({
            currentLocale,
            route: eventData.route,
            baseUrl: 'https://sejarah-thumann.online'
        });
        headObject.link.push(...hreflangs);
    }

    if (jsonLdScript) {
        headObject.script = [jsonLdScript];
    }

    applyHead('event-seo', headObject);
}

/**
 * Apply BreadcrumbList structured data for navigation hierarchy.
 * 
 * @param {Array<Object>} items - Array of breadcrumb items.
 * @param {string} items[].name - The name of the breadcrumb item.
 * @param {string} items[].url - The URL of the breadcrumb item.
 */
export function applyBreadcrumbSeo(items) {
    if (!items || items.length === 0) return;

    const normalizedItems = items
        .map(item => ({
            name: typeof item?.name === 'string' ? item.name.trim() : '',
            url: typeof item?.url === 'string' ? item.url.trim() : ''
        }))
        .filter(item => item.name.length > 0);

    if (normalizedItems.length < 2) return;

    const jsonLdScript = {
        type: 'application/ld+json',
        children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": normalizedItems.map((item, idx) => {
                const listItem = {
                    "@type": "ListItem",
                    "position": idx + 1,
                    "name": item.name
                };

                if (item.url) {
                    listItem.item = item.url;
                }

                return listItem;
            })
        }),
        key: 'breadcrumb-schema'
    };

    applyHead('breadcrumb-seo', {
        script: [jsonLdScript]
    });
}
/**
 * Apply SEO tags for person pages.
 * This includes standard meta tags plus structured data (JSON-LD) for a Person.
 * 
 * @param {Object} person - The person object containing properties like firstname, lastname, etc.
 * @param {string} [person.currentLocale='nl'] - Current locale for canonical URL.
 * @param {string} [person.personId] - Person ID for constructing route.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.includeTwitter=false] - Whether to include Twitter Card meta tags.
 * @param {boolean} [options.includeJsonLd=false] - Whether to include structured data for Person.
 * @param {boolean} [options.includeHreflangs=false] - Whether to include hreflang tags.
 * @param {string} [options.canonicalUrl] - Optional override for canonical URL.
 */
export function applyPersonSeo(person, options = {}) {
    if (!person) return;

    const description = options.description
        || `Bekijk de familiegeschiedenis van ${person.firstname} ${person.lastname}. Ontdek verhalen, foto's, historische documenten en archiefstukken.`;
    const title = `${person.firstname} ${person.lastname} | Sejarah Thümann`;
    const image = person.profileImage || `https://sejarah-thumann.online/default-image.jpg`;

    const personId = person.personId || person.id || options.personId;
    const currentLocale = person.currentLocale || options.currentLocale || 'nl';

    const canonicalUrl = options.canonicalUrl || buildCanonicalUrl({
        currentLocale,
        route: `/person/${personId}`,
        baseUrl: 'https://sejarah-thumann.online'
    });

    // Build JSON-LD structured data if requested
    let jsonLdScript = null;
    if (options.includeJsonLd) {
        jsonLdScript = {
            type: 'application/ld+json',
            children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Person",
                "name": `${person.firstname} ${person.lastname}`,
                "alternateName": person.nickname || undefined,
                "gender": person.sex === 'M' ? 'Male' : person.sex === 'F' ? 'Female' : undefined,
                "birthDate": person.birthdatetr || undefined,
                "deathDate": person.deathdatetr || undefined,
                "birthPlace": person.birthplace || undefined,
                "deathPlace": person.deathplace || undefined,
                "jobTitle": person.occupations?.[0] || undefined,
                "spouse": person.spouses?.map(spouse => ({
                    "@type": "Person",
                    "name": spouse.name,
                    "url": buildCanonicalUrl({
                        currentLocale,
                        route: `/person/${spouse.personID}`,
                        baseUrl: 'https://sejarah-thumann.online'
                    })
                })),
                "image": image,
                "url": canonicalUrl
            })
        };
    }

    const headObject = {
        title: title,
        meta: [
            { name: 'description', content: description },
            { property: 'og:type', content: 'profile' },
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:image', content: image },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'og:image:type', content: 'image/jpeg' },
            { property: 'og:url', content: canonicalUrl },
        ],
        link: [
            { rel: 'canonical', href: canonicalUrl }
        ]
    };

    if (options.includeTwitter) {
        headObject.meta.push(
            { name: 'twitter:title', content: title },
            { name: 'twitter:description', content: description },
            { name: 'twitter:image', content: image },
            { name: 'twitter:card', content: 'summary_large_image' }
        );
    }

    if (options.includeHreflangs && personId) {
        const hreflangs = buildHreflangs({
            currentLocale,
            route: `/person/${personId}`,
            baseUrl: 'https://sejarah-thumann.online'
        });
        headObject.link.push(...hreflangs);
    }

    if (jsonLdScript) {
        headObject.script = [jsonLdScript];
    }

    applyHead('person-seo', headObject);
}

/**
 * Apply SEO tags for place pages.
 * This includes Place schema JSON-LD and meta robots="noindex" to prevent indexing.
 * 
 * @param {Object} place - The place object containing properties like name, latitude, longitude, etc.
 * @param {string} place.name - The name of the place.
 * @param {string} [place.description] - Description of the place.
 * @param {number} [place.latitude] - Latitude coordinate.
 * @param {number} [place.longitude] - Longitude coordinate.
 * @param {string} [place.image] - Image URL for the place.
 * @param {number} [place.eventCount] - Number of events associated with this place.
 * @param {string} [place.placeId] - Place ID for constructing route.
 * @param {string} [place.currentLocale='nl'] - Current locale for canonical URL.
 * @param {Object} [options] - Optional settings.
 * @param {boolean} [options.includeJsonLd=false] - Whether to include JSON-LD structured data.
 * @param {boolean} [options.includeHreflangs=false] - Whether to include hreflang tags.
 * @param {boolean} [options.noindex=true] - Whether to add meta robots="noindex" (default true).
 * @param {string} [options.canonicalUrl] - Optional override for canonical URL.
 */
export function applyPlaceSeo(place, options = {}) {
    if (!place) return;

    const title = `${place.name} | Sejarah Thümann`;
    const description = place.description || `Ontdek alle gebeurtenissen en familieleden gerelateerd aan ${place.name}.`;
    const image = place.image || `https://sejarah-thumann.online/default-image.jpg`;

    const placeId = place.placeId || place.id || options.placeId;
    const currentLocale = place.currentLocale || options.currentLocale || 'nl';
    const noindex = options.noindex !== false; // Default to true

    const canonicalUrl = options.canonicalUrl || buildCanonicalUrl({
        currentLocale,
        route: `/plaatsen/${placeId}`,
        baseUrl: 'https://sejarah-thumann.online'
    });

    // Build JSON-LD structured data if requested
    let jsonLdScript = null;
    if (options.includeJsonLd) {
        const placeSchema = {
            "@context": "https://schema.org",
            "@type": "Place",
            "name": place.name,
            "description": description,
            "url": canonicalUrl,
            "image": image
        };

        // Add coordinates if available
        if (place.latitude && place.longitude) {
            placeSchema.geo = {
                "@type": "GeoCoordinates",
                "latitude": place.latitude,
                "longitude": place.longitude
            };
        }

        // Add event count as a custom property
        if (place.eventCount) {
            placeSchema.eventCount = place.eventCount;
        }

        jsonLdScript = {
            type: 'application/ld+json',
            children: JSON.stringify(placeSchema)
        };
    }

    const headObject = {
        title: title,
        meta: [
            { name: 'description', content: description },
            { property: 'og:type', content: 'website' },
            { property: 'og:title', content: title },
            { property: 'og:description', content: description },
            { property: 'og:image', content: image },
            { property: 'og:image:width', content: '1200' },
            { property: 'og:image:height', content: '630' },
            { property: 'og:image:type', content: 'image/jpeg' },
            { property: 'og:url', content: canonicalUrl },
        ],
        link: [
            { rel: 'canonical', href: canonicalUrl }
        ]
    };

    // Add noindex meta tag if requested
    if (noindex) {
        headObject.meta.push({ name: 'robots', content: 'noindex' });
    }

    // Add hreflang tags if requested (even though not indexed, useful for internal linking)
    if (options.includeHreflangs && placeId) {
        const hreflangs = buildHreflangs({
            currentLocale,
            route: `/plaatsen/${placeId}`,
            baseUrl: 'https://sejarah-thumann.online'
        });
        headObject.link.push(...hreflangs);
    }

    if (jsonLdScript) {
        headObject.script = [jsonLdScript];
    }

    applyHead('place-seo', headObject);
}
